// Property accessors
import { getClonedElementType, getElementType } from './elementPropertyUtils';

// Constants
import { ALIAS_TYPE } from '../../alias/aliasConstants';
import { BOARD_TYPE } from '../../boards/boardConstants';
import { COLUMN_TYPE } from '../../columns/columnConstants';
import { CARD_TYPE } from '../../cards/cardConstants';
import { LINK_TYPE } from '../../links/linkConstants';
import { LINE_TYPE } from '../../lines/lineConstants';
import { SKELETON_ELEMENT_TYPE } from '../elementConstants';
import { IMAGE_TYPE } from '../../images/imageConstants';
import { FILE_TYPE } from '../../files/fileConstants';
import { COMMENT_THREAD_TYPE } from '../../comments/commentConstants';
import { TASK_LIST_TYPE } from '../../taskLists/taskListConstants';
import { TASK_TYPE } from '../../tasks/tasksConstants';
import { CLONE_TYPE } from '../../clone/cloneConstants';
import { SKETCH_TYPE } from '../../drawings/sketches/sketchConstants';
import { ANNOTATION_TYPE } from '../../drawings/annotations/annotationConstants';
import { COLOR_SWATCH_TYPE } from '../../colorSwatches/colorSwatchesConstants';
import { DOCUMENT_TYPE } from '../../documents/documentConstants';
import { TABLE_TYPE } from '../../table/tableConstants';

// Element Type functions
export const isElementType = (type) => (elementType) => getElementType(elementType) === type;

export const isSkeleton = isElementType(SKELETON_ELEMENT_TYPE);

export const isCard = isElementType(CARD_TYPE);
export const isDocument = isElementType(DOCUMENT_TYPE);
export const isLink = isElementType(LINK_TYPE);
export const isImage = isElementType(IMAGE_TYPE);
export const isFile = isElementType(FILE_TYPE);
export const isTaskList = isElementType(TASK_LIST_TYPE);
export const isTask = isElementType(TASK_TYPE);
export const isSketch = isElementType(SKETCH_TYPE);
export const isColorSwatch = isElementType(COLOR_SWATCH_TYPE);
export const isTable = isElementType(TABLE_TYPE);

export const isColumn = isElementType(COLUMN_TYPE);
export const isAlias = isElementType(ALIAS_TYPE);
export const isClone = isElementType(CLONE_TYPE);
export const isBoard = isElementType(BOARD_TYPE);

export const isLine = isElementType(LINE_TYPE);
export const isAnnotation = isElementType(ANNOTATION_TYPE);

export const isCommentThread = isElementType(COMMENT_THREAD_TYPE);

export const isBoardLike = (elementType) => isBoard(elementType) || isAlias(elementType);
export const isTaskLike = (elementType) => isTaskList(elementType) || isTask(elementType);
export const isElementLinkable = (elementType) => isClone(elementType) || isAlias(elementType);

export const isDocumentLike = (elementType) =>
    isDocument(elementType) || (isClone(elementType) && getClonedElementType(elementType) === DOCUMENT_TYPE);

export const isDrawing = (elementType) => isSketch(elementType) || isAnnotation(elementType);

/**
 * Determines if the specified element type can be cloned.
 */
export const canBeCloned = (elementType) => isCard(elementType) || isDocument(elementType) || isClone(elementType);

/**
 * The only elements that can't have descendants, are comments and lines, now that comments can be attached
 * to other elements.
 */
export const hasDescendants = (elementType) => !isCommentThread(elementType) && !isLine(elementType);

/**
 * Determines if the element type can have child elements that would be visible if displayed on a board,
 * excluding elements that can only have "Attached" elements (like comments).
 * For example, the only child elements that a card can have is a comment thread.
 * Columns on the other hand can have many elements as children that are visible to users when viewing a board.
 * Boards or aliases can have many elements as children, but none of them are visible to users when viewing a board.
 */
export const hasVisibleDescendantsExcludingAttachments = (elementType) =>
    isColumn(elementType) || isTaskList(elementType) || isTask(elementType);

/**
 * The same as hasVisibleDescendantsExcludingAttachments except this includes any elements that can have
 * attachments as well, such as cards which can have comment threads attached to them.
 */
export const hasVisibleDescendants = (elementType) =>
    hasVisibleDescendantsExcludingAttachments(elementType) ||
    isCard(elementType) ||
    isDocument(elementType) ||
    isLink(elementType) ||
    isImage(elementType) ||
    isFile(elementType) ||
    isSketch(elementType) ||
    isColorSwatch(elementType);

export const isContainer = (elementType) =>
    isBoard(elementType) ||
    isAlias(elementType) ||
    isColumn(elementType) ||
    isTaskList(elementType) ||
    isTask(elementType);

export const isContent = (elementType) => {
    // elementType here might an element, so need to get the actual element type if so
    const elType = getElementType(elementType);

    switch (elType) {
        case COLOR_SWATCH_TYPE:
        case CARD_TYPE:
        case DOCUMENT_TYPE:
        case LINK_TYPE:
        case IMAGE_TYPE:
        case FILE_TYPE:
        case TASK_LIST_TYPE:
        case SKETCH_TYPE:
        case TABLE_TYPE:
            return true;
        default:
            return false;
    }
};
